import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Building2, Users, Loader2, CheckCircle2 } from 'lucide-react';
import { acceptOrgInvite } from '@/services/inviteApi';

interface JoinOrganizationStepProps {
  inviteCode: string;
  organizationName: string;
  teamName?: string;
  onComplete: (data: {
    organization_id: string;
    organization_name: string;
    team_id?: string;
    team_name?: string;
  }) => void;
}

export function JoinOrganizationStep({
  inviteCode,
  organizationName,
  teamName,
  onComplete,
}: JoinOrganizationStepProps) {
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleJoin = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const result = await acceptOrgInvite(inviteCode);
      onComplete({
        organization_id: result.organization_id,
        organization_name: result.organization_name,
        team_id: result.team_id,
        team_name: result.team_name,
      });
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.message || 'Failed to join organization';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="flex justify-center">
          <CheckCircle2 className="h-12 w-12 text-green-500" />
        </div>
        <h2 className="text-xl font-semibold">Join {organizationName}</h2>
        <p className="text-sm text-muted-foreground">
          You've been invited to join this organization.
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
        <div className="flex items-center gap-3">
          <Building2 className="h-5 w-5 text-muted-foreground" />
          <div>
            <p className="text-sm font-medium">Organization</p>
            <p className="text-sm text-muted-foreground">{organizationName}</p>
          </div>
        </div>

        {teamName && (
          <div className="flex items-center gap-3">
            <Users className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">Team</p>
              <p className="text-sm text-muted-foreground">{teamName}</p>
            </div>
          </div>
        )}
      </div>

      <Button onClick={handleJoin} className="w-full" disabled={isSubmitting}>
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Joining...
          </>
        ) : (
          'Accept & Continue'
        )}
      </Button>
    </div>
  );
}
